/* === AgBadge -- Anagogia Design System === */

import { useEffect, useRef, useState } from 'react';

import { AG_LABELS } from '../utils/agLabels';

import styles from './AgBadge.module.css';

interface AgBadgeProps {
  count?: number;
  color?: 'primary' | 'secondary' | 'error' | 'warning';
  max?: number;
  invisible?: boolean;
  dot?: boolean;
  children: React.ReactNode;
  className?: string;
  /**
   * Nom accessible de la pastille. Par défaut « N notifications » (ou
   * « Notification » pour `dot`) : à fournir dès que le badge compte autre chose
   * que des notifications (ex. « Importance 3 »).
   */
  'aria-label'?: string;
}

const COLOR_MAP: Record<NonNullable<AgBadgeProps['color']>, string> = {
  primary: styles.colorPrimary,
  secondary: styles.colorSecondary,
  error: styles.colorError,
  warning: styles.colorWarning,
};

/**
 * Badge de comptage ou de statut ancré à son élément enfant.
 * @category Affichage
 */
function AgBadge({
  count,
  color = 'primary',
  max = 99,
  invisible = false,
  dot = false,
  children,
  className,
  'aria-label': ariaLabel,
}: AgBadgeProps) {
  const [bounce, setBounce] = useState(false);
  const prevCountRef = useRef(count);

  // Trigger bounce animation when count changes
  useEffect(() => {
    if (count !== undefined && prevCountRef.current !== count && count > 0) {
      setBounce(true);
      const timer = setTimeout(() => setBounce(false), 300);
      prevCountRef.current = count;
      return () => clearTimeout(timer);
    }
    prevCountRef.current = count;
  }, [count]);

  const isHidden = invisible || (!dot && (count === undefined || count === 0));

  const displayValue =
    dot || count === undefined
      ? null
      : count > max
        ? `${max}+`
        : String(count);

  const colorClass = COLOR_MAP[color] ?? COLOR_MAP.primary;

  return (
    <span
      className={[styles.wrapper, className].filter(Boolean).join(' ')}
    >
      {children}
      {!isHidden && (
        <span
          className={[
            styles.badge,
            dot ? styles.dot : undefined,
            colorClass,
            bounce ? styles.bounce : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={
            ariaLabel ??
            (dot
              ? AG_LABELS.notification
              : displayValue
                ? AG_LABELS.notifications(displayValue)
                : undefined)
          }
        >
          {displayValue}
        </span>
      )}
    </span>
  );
}

export { AgBadge };
export type { AgBadgeProps };
export default AgBadge;
